import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import CustomDomainsCore from '../components/CustomDomainsCore';
import { CustomDomain } from '../widget/types';

const CustomDomainsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceId = parseInt(id || '0', 10);
  const { showSuccess, showError } = useToast();

  const [workspace, setWorkspace] = useState<any>(null);
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [tierLimit, setTierLimit] = useState<{ current: number; limit: number; unlimited: boolean }>({ current: 0, limit: 0, unlimited: true });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wsResponse, domainsResponse] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}`),
        apiService.get(`/workspaces/${workspaceId}/domains`),
      ]);
      setWorkspace(wsResponse.data);
      const raw = domainsResponse.data;
      const domainList = raw.domains ? raw.domains : (raw || []);
setDomains(domainList.filter((d: any) => !d.domain.endsWith('.dashboardfox.app') && !d.domain.endsWith('.yurbi.app')));
      if (raw.tierLimit) setTierLimit(raw.tierLimit);
    } catch (err: any) {
      showError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const domainsResponse = await apiService.get(`/workspaces/${workspaceId}/domains`);
      const raw = domainsResponse.data;
      const domainList = raw.domains ? raw.domains : (raw || []);
setDomains(domainList.filter((d: any) => !d.domain.endsWith('.dashboardfox.app') && !d.domain.endsWith('.yurbi.app')));
      if (raw.tierLimit) setTierLimit(raw.tierLimit);
    } finally {
      setRefreshing(false);
    }
  };

const handleAddDomain = async (domain: string) => {
    await apiService.post(`/workspaces/${workspaceId}/domains`, { domain });
    const domainsResponse = await apiService.get(`/workspaces/${workspaceId}/domains`);
    const raw = domainsResponse.data;
    const domainList = raw.domains ? raw.domains : (raw || []);
setDomains(domainList.filter((d: any) => !d.domain.endsWith('.dashboardfox.app') && !d.domain.endsWith('.yurbi.app')));
    if (raw.tierLimit) setTierLimit(raw.tierLimit);
  };

  const handleDeleteDomain = async (domainId: number) => {
    await apiService.delete(`/workspaces/${workspaceId}/domains/${domainId}`);
    await loadData();
  };

  return (
    <div
      style={{
        padding: '32px',
        backgroundColor: 'var(--v2-bg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <nav
        style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
        }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '0.875rem',
            color: 'var(--v2-text-tertiary)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </button>
	<ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontWeight: 500 }}>{workspace?.name}</span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Custom Domains</span>
      </nav>

    <CustomDomainsCore
        domains={domains}
        tierLimit={tierLimit}
        loading={loading}
        error={null}
        refreshing={refreshing}
        mode="modal"
        onAddDomain={handleAddDomain}
        onDeleteDomain={handleDeleteDomain}
        onRefresh={handleRefresh}
        onSuccess={showSuccess}
        onError={showError}
      />
    </div>
  );
};

export default CustomDomainsPage;
